import { notification } from 'antd'
import { generateMessageError } from 'modules/core/utils'
import i18n from 'i18next'
import { saveAs } from 'file-saver'

export const onConfirmUpload = ({
  setIsLoading,
  breadcrumbPath,
  uploadFileS3,
  fileList,
  uploadUrlAws,
  onRefetch,
  onClose,
  setAuditS3,
  onRefetcAuditList,
  enableOldS3,
}) => {
  const folders = breadcrumbPath.length > 0 ? breadcrumbPath.map((el) => el.title).join('/') : ''

  setIsLoading(true)
  uploadFileS3(
    { 'content-type': fileList[0].type },
    { file_name: fileList[0].name, file_prefix: folders },
  )
    .then((response) => {
      uploadUrlAws(response, fileList[0], enableOldS3).then((response2) => {
        if (response2.status === 204) {
          Promise.all([
            setAuditS3({ file_name: fileList[0].name, operation: 'upload' }),
            onRefetch(),
          ]).then(() => {
            onRefetcAuditList()
            notification.success({
              description: i18n.t('FEEDBACK_UPLOAD_FILE_SUCCESS', {
                configModule: fileList[0].name,
              }),
              duration: 8,
            })
            onClose()
          })
        } else {
          notification.error({
            message: i18n.t('FEEDBACK_DEFAULT_ERROR'),
            description: generateMessageError(response2, 'FEEDBACK_UPLOAD_FILE_ERROR', {
              fileName: fileList[0].name,
            }),
            duration: 0,
          })
          onClose()
        }
      })
    })
    .catch((error) => {
      notification.error({
        message: i18n.t('FEEDBACK_DEFAULT_ERROR'),
        description: generateMessageError(error, 'FEEDBACK_UPLOAD_FILE_ERROR', {
          fileName: fileList[0].name,
        }),
        duration: 0,
      })
      onClose()
    })
}

export const addFolder = (data, parentKey, folderName2) => {
  const generateKey = (parentKey, childrenCount) => {
    return parentKey ? `${parentKey}.${childrenCount}` : `${childrenCount}`
  }

  const traverseAndAdd = (nodes) => {
    return nodes.map((node) => {
      if (node.key === parentKey) {
        const children = node.children || []
        const newKey = generateKey(parentKey, children.length)

        const newFolder = {
          title: folderName2,
          key: newKey,
          type: 'folder',
          isLeaf: false,
          children: [],
        }

        return {
          ...node,
          children: [...children, newFolder],
        }
      }

      if (node.children) {
        return {
          ...node,
          children: traverseAndAdd(node.children),
        }
      }

      return node
    })
  }

  if (!parentKey) {
    const newKey = generateKey(null, data.length)
    const newFolder = {
      title: folderName2,
      key: newKey,
      type: 'folder',
      isLeaf: false,
      children: [],
    }
    return [...data, newFolder]
  }

  return traverseAndAdd(data)
}

const findMinimalPathToChild = (data, key, currentPath = []) => {
  for (const node of data) {
    const newPath = [...currentPath, node]

    if (node.children && node.children.length > 0) {
      const childFound = node.children.find((child) => child.key === key)

      if (childFound) {
        return [...newPath, childFound]
      }

      const found = findMinimalPathToChild(node.children, key, newPath)
      if (found) return found
    }
  }
  return null
}

const getMinimalChildPathInfo = (data, key) => {
  const minimalPath = findMinimalPathToChild(data, key)

  if (!minimalPath) {
    return {
      path: [],
      names: [],
      childNode: null,
      exists: false,
    }
  }

  const childNode = minimalPath[minimalPath.length - 1]

  return {
    path: minimalPath,
    names: minimalPath.map((node) => node.title),
    childNode: childNode,
    exists: true,
  }
}

export const handleDownload = async ({
  key,
  record,
  directoriesData,
  downloadFileS3,
  loggedUser,
  setAuditS3,
  onRefetcAuditList,
}) => {
  try {
    const childInfo = getMinimalChildPathInfo(directoriesData, key)

    const minimalPath = childInfo.names.length > 0 ? childInfo.names.join('/') : record.nombre

    const response = await downloadFileS3({ file_key: `${loggedUser.society}/${minimalPath}` })
    const response2 = await fetch(response.payload.data.url, {
      method: 'GET',
    })

    const blob = await response2.blob()

    saveAs(blob, record.nombre)
    setAuditS3({ file_name: record.nombre, operation: 'download' }).then(() => {
      onRefetcAuditList()
    })
  } catch (error) {
    notification.error({
      message: i18n.t('FEEDBACK_DEFAULT_ERROR'),
      description: generateMessageError(error),
      duration: 0,
    })
  }
}

export const generateTableData = (selectedKeys, directoriesData) => {
  if (selectedKeys.length > 0) {
    const selectedKey = selectedKeys[0]
    const path = findPathByKey(directoriesData, selectedKey)

    if (!path) return []

    const selectedNode = path[path.length - 1]

    let children = []

    if (selectedNode.children && selectedNode.children.length > 0) {
      children = selectedNode.children
    } else if (path.length > 1) {
      const parentNode = path[path.length - 2]
      children = parentNode.children || []
    } else if (!selectedNode.children) {
      children = directoriesData
    }

    return children.map((child) => ({
      key: child.key,
      nombre: child.title,
      tipo: child.type,
      tamaño: child.size ? `${child.size} bytes` : '',
      fecha: child.timestamp ? new Date(child.timestamp).toLocaleString() : '',
      esHoja: child.isLeaf ? 'Sí' : 'No',
    }))
  } else {
    return directoriesData.map((node) => ({
      key: node.key,
      nombre: node.title,
      tipo: node.type,
      tamaño: node.size ? `${node.size} bytes` : '',
      fecha: node.timestamp ? new Date(node.timestamp).toLocaleString() : '',
      esHoja: node.isLeaf ? 'Sí' : 'No',
    }))
  }
}

export const findNodeByKey = (data, key) => {
  for (let node of data) {
    if (node.key === key) return node
    if (node.children) {
      const found = findNodeByKey(node.children, key)
      if (found) return found
    }
  }
  return null
}

export const findPathByKey = (data, key, path = []) => {
  for (let node of data) {
    if (node.key === key) {
      return [...path, node]
    }
    if (node.children) {
      const found = findPathByKey(node.children, key, [...path, node])
      if (found) return found
    }
  }
  return null
}

export const transformData = (data) => {
  return data.map((item) => {
    const transformedItem = {
      title: item.name,
      key: item.position,
      type: item.type,
      isLeaf: item.isLeaf,
      children: item.children ? transformData(item.children) : null,
      ...(item.size && { size: item.size }),
      ...(item.timestamp && { timestamp: item.timestamp }),
    }

    return transformedItem
  })
}
